import { borrowMarketsQueryParams } from '@/config/query/queryParams'
import {
  BORROW_LIQUIDITY_FROM_BOUND_KEY,
  BORROW_LIQUIDITY_TO_BOUND_KEY,
  BORROW_MARKET_SIZE_FROM_BOUND_KEY,
  BORROW_MARKET_SIZE_TO_BOUND_KEY,
} from '@/constants/state'
import { useMixpanel } from '@/context/mixpanel'
import type { BorrowMarketRow } from '@/hooks/useBorrowMarkets'
import { type NumberRangeSearchState, useBorrowMarketsSearchState } from '@/hooks/useBorrowMarketsSearchState'
import { useDebounce } from '@/hooks/useDebounce'
import type { PopoverProps } from '@radix-ui/react-popover'
import { formatAmount } from '@vaultsfyi/common/utils'
import { useEffect, useMemo, useState } from 'react'
import { AccordionItem } from '../common/accordion'
import { Popover, PopoverContent } from '../common/popover'
import { FilterAccordionContent, FilterAccordionTrigger, FilterSelectTrigger } from '../shared/filterSelect'
import { UsdRangeSelector } from '../shared/usdRangeSelector'

type BorrowMarketsRangeFilterType = 'marketSize' | 'availableToBorrow'

interface BorrowMarketsRangeFilterProps extends Pick<PopoverProps, 'modal'> {
  borrowMarkets: BorrowMarketRow[]
  type: BorrowMarketsRangeFilterType
  className?: string
}

interface BorrowMarketsRangeFilterConfig {
  label: string
  filterName: string
  fromKey: string
  toKey: string
  getValue: (borrowMarket: BorrowMarketRow) => number | undefined
  getSelectedRange: (searchState: ReturnType<typeof useBorrowMarketsSearchState>) => NumberRangeSearchState
  minValue?: number
}

export const BorrowMarketsRangeFilter = ({ borrowMarkets, type, className, modal }: BorrowMarketsRangeFilterProps) => {
  const config = RANGE_FILTER_CONFIG[type]
  const defaultRange = useBorrowMarketsDefaultRange({
    borrowMarkets,
    getValue: config.getValue,
    minValue: config.minValue,
  })
  const searchState = useBorrowMarketsSearchState()
  const selectedRange = config.getSelectedRange(searchState)
  const sliderRange = useRangeSliderState(selectedRange, defaultRange)

  return (
    <Popover
      modal={modal}
      onOpenChange={() => {
        sliderRange.reset()
      }}
    >
      <FilterSelectTrigger className={className}>
        <div className="flex w-full max-w-full items-center gap-2 truncate">
          <TriggerText label={config.label} sliderRange={sliderRange.value} defaultRange={defaultRange} />
        </div>
      </FilterSelectTrigger>
      <PopoverContent className="flex w-56 flex-col gap-1 p-4">
        <BorrowMarketsRangeFilterContent config={config} defaultRange={defaultRange} sliderRange={sliderRange} />
      </PopoverContent>
    </Popover>
  )
}

export const BorrowMarketsRangeFilterAccordion = ({ borrowMarkets, type }: BorrowMarketsRangeFilterProps) => {
  const config = RANGE_FILTER_CONFIG[type]
  const defaultRange = useBorrowMarketsDefaultRange({
    borrowMarkets,
    getValue: config.getValue,
    minValue: config.minValue,
  })
  const searchState = useBorrowMarketsSearchState()
  const selectedRange = config.getSelectedRange(searchState)
  const sliderRange = useRangeSliderState(selectedRange, defaultRange)

  return (
    <AccordionItem value={type}>
      <FilterAccordionTrigger name={config.label} />
      <FilterAccordionContent>
        <div className="flex flex-col gap-1 p-2">
          <BorrowMarketsRangeFilterContent config={config} defaultRange={defaultRange} sliderRange={sliderRange} />
        </div>
      </FilterAccordionContent>
    </AccordionItem>
  )
}

interface BorrowMarketsRangeFilterContentProps {
  config: BorrowMarketsRangeFilterConfig
  defaultRange: [number, number]
  sliderRange: RangeSliderState
}

const BorrowMarketsRangeFilterContent = ({
  config,
  defaultRange,
  sliderRange,
}: BorrowMarketsRangeFilterContentProps) => {
  const { updateSearchState } = useBorrowMarketsSearchState()
  const { trackEvent } = useMixpanel()

  useDebounce(
    () => {
      const entries: [string, number | undefined][] = []
      const from = sliderRange.value[0] === defaultRange[0] ? undefined : sliderRange.value[0]
      const to = sliderRange.value[1] === defaultRange[1] ? undefined : sliderRange.value[1]

      if (from !== sliderRange.selectedRange.from) {
        entries.push([config.fromKey, from])
      }
      if (to !== sliderRange.selectedRange.to) {
        entries.push([config.toKey, to])
      }

      if (entries.length === 0) {
        return
      }

      updateSearchState(...entries)
      trackEvent('Filter used', {
        Filter: config.filterName,
        Value: `${sliderRange.value[0]} - ${sliderRange.value[1]}`,
      })
    },
    [config, defaultRange, sliderRange, updateSearchState, trackEvent],
    500
  )

  return (
    <>
      <span className="text-sm">{config.label} range, USD</span>
      <UsdRangeSelector
        initialValue={sliderRange.value}
        minValue={defaultRange[0]}
        maxValue={defaultRange[1]}
        onChange={sliderRange.setValue}
      />
    </>
  )
}

interface UseBorrowMarketsDefaultRangeProps {
  borrowMarkets: BorrowMarketRow[]
  getValue: (borrowMarket: BorrowMarketRow) => number | undefined
  minValue?: number
}

const useBorrowMarketsDefaultRange = ({ borrowMarkets, getValue, minValue = 0 }: UseBorrowMarketsDefaultRangeProps) =>
  useMemo(() => {
    const maxValue = Math.ceil(Math.max(minValue, ...borrowMarkets.map((borrowMarket) => getValue(borrowMarket) ?? 0)))

    return [minValue, Math.max(minValue + 1, maxValue)] as [number, number]
  }, [borrowMarkets, getValue, minValue])

interface RangeSliderState {
  value: [number, number]
  selectedRange: NumberRangeSearchState
  setValue: (value: [number, number]) => void
  reset: () => void
}

const useRangeSliderState = (
  selectedRange: NumberRangeSearchState,
  defaultRange: [number, number]
): RangeSliderState => {
  const selectedSliderRange = useMemo(
    () => [selectedRange.from ?? defaultRange[0], selectedRange.to ?? defaultRange[1]] as [number, number],
    [defaultRange, selectedRange.from, selectedRange.to]
  )
  const [value, setValue] = useState(selectedSliderRange)

  useEffect(() => {
    setValue(selectedSliderRange)
  }, [selectedSliderRange])

  return {
    value,
    selectedRange,
    setValue,
    reset: () => setValue(selectedSliderRange),
  }
}

interface TriggerTextProps {
  label: string
  sliderRange: [number, number]
  defaultRange: [number, number]
}

const TriggerText = ({ label, sliderRange, defaultRange }: TriggerTextProps) => {
  if (sliderRange[0] === defaultRange[0] && sliderRange[1] === defaultRange[1]) {
    return <span>{label}</span>
  }

  const lowerBoundText = formatAmount(sliderRange[0], { notation: 'compact' })
  const upperBoundText = formatAmount(sliderRange[1], { notation: 'compact' })

  if (sliderRange[0] !== defaultRange[0] && sliderRange[1] === defaultRange[1]) {
    return (
      <span>
        {label} {'>'} {lowerBoundText}
      </span>
    )
  }

  if (sliderRange[0] === defaultRange[0] && sliderRange[1] !== defaultRange[1]) {
    return (
      <span>
        {label} {'<'} {upperBoundText}
      </span>
    )
  }

  return (
    <span>
      {label}: {lowerBoundText} – {upperBoundText}
    </span>
  )
}

const RANGE_FILTER_CONFIG: Record<BorrowMarketsRangeFilterType, BorrowMarketsRangeFilterConfig> = {
  marketSize: {
    label: 'Market size',
    filterName: 'borrow market size range',
    fromKey: BORROW_MARKET_SIZE_FROM_BOUND_KEY,
    toKey: BORROW_MARKET_SIZE_TO_BOUND_KEY,
    getValue: (borrowMarket) => borrowMarket.marketSizeUsd,
    getSelectedRange: (searchState) => searchState.selectedMarketSizeRange,
    minValue: borrowMarketsQueryParams.minTvl,
  },
  availableToBorrow: {
    label: 'Available to borrow',
    filterName: 'borrowable amount range',
    fromKey: BORROW_LIQUIDITY_FROM_BOUND_KEY,
    toKey: BORROW_LIQUIDITY_TO_BOUND_KEY,
    getValue: (borrowMarket) => borrowMarket.availableToBorrowUsd,
    getSelectedRange: (searchState) => searchState.selectedLiquidityRange,
  },
}
